import { TableRow, TableCell } from '@mui/material';
import { AnyPlan } from '../../types';
import {
  getDigitalBudgetData,
  getScenarios,
  getSegmentsJoined,
  getTargetGroups,
  getTVStatusValues,
  TVStatusValue,
} from './helpers';

type CountryMetaSectionProps = {
  country: string;
  plans: AnyPlan[];
  firstColWidth: number;
  getCountryData: (plan: AnyPlan, country: string) => any;
  formatBudget: (v?: number, currency?: string) => string;
  showOnlyDifferences: boolean;
};

function isDifferentFromBase<T>(
  values: (T | undefined | null)[],
  index: number,
  equals: (a: T, b: T) => boolean = (a, b) => a === b
): boolean {
  if (index === 0 || values.length === 0) return false;
  const base = values[0];
  const current = values[index];

  if (base == null && current == null) return false;
  if ((base == null) !== (current == null)) return true;

  return !equals(base as T, current as T);
}

function hasAnyDifference<T>(
  values: (T | undefined | null)[],
  equals?: (a: T, b: T) => boolean
): boolean {
  for (let i = 1; i < values.length; i += 1) {
    if (isDifferentFromBase(values, i, equals as any)) return true;
  }
  return false;
}

export default function CountryMetadataSection({
  country,
  plans,
  firstColWidth,
  getCountryData,
  formatBudget,
  showOnlyDifferences,
}: CountryMetaSectionProps) {
  const scenarios = getScenarios(plans, country, getCountryData);
  const targetGroups = getTargetGroups(plans, country, getCountryData);
  const segmentsJoined = getSegmentsJoined(plans, country, getCountryData);
  const tvStatusValues = getTVStatusValues(plans, country, getCountryData);
  const tvStatusStrings = tvStatusValues.map(
    (value: TVStatusValue) => `${value.main}|${value.sub}`
  );

  const { totals: digitalBudgetTotals, sums: digitalBudgetSums } =
    getDigitalBudgetData(plans, country, getCountryData);

  return (
    <>
      {/* SCENARIO */}
      {(!showOnlyDifferences || hasAnyDifference<string>(scenarios)) && (
        <TableRow sx={{ mx: 4, height: '60px' }}>
          <TableCell
            sx={{
              width: firstColWidth,
              fontWeight: 700,
              fontSize: '14px',
              borderLeft: '1px solid',
              borderColor: '#C7D8F9',
            }}
          >
            Scenario
          </TableCell>
          {plans.map((plan, index) => {
            const scenario = scenarios[index];
            const diff = isDifferentFromBase<string>(scenarios, index);

            return (
              <TableCell
                key={`sc-${plan.id}`}
                sx={{
                  borderLeft: '1px solid',
                  borderRight: '1px solid',
                  borderColor: '#C7D8F9',
                  fontSize: '12px',
                  textAlign: 'center',
                  verticalAlign: 'center',
                  color: diff ? 'error.main' : '#525F81',
                }}
              >
                {scenario}
              </TableCell>
            );
          })}
        </TableRow>
      )}

      {/* TARGET GROUP */}
      {(!showOnlyDifferences || hasAnyDifference<string>(targetGroups)) && (
        <TableRow sx={{ mx: 4, bgColor: '#F5F5F5', height: '60px' }}>
          <TableCell
            sx={{
              fontWeight: 700,
              fontSize: '14px',
              borderLeft: '1px solid',
              borderColor: '#C7D8F9',
            }}
          >
            Campaign target group
          </TableCell>
          {plans.map((plan, index) => {
            const tg = targetGroups[index];
            const diff = isDifferentFromBase<string>(targetGroups, index);

            return (
              <TableCell
                key={`tg-${plan.id}`}
                sx={{
                  borderLeft: '1px solid',
                  borderRight: '1px solid',
                  borderColor: '#C7D8F9',
                  fontWeight: 400,
                  fontSize: '12px',
                  textAlign: 'center',
                  verticalAlign: 'center',
                  color: diff ? 'error.main' : '#525F81',
                }}
              >
                {tg}
              </TableCell>
            );
          })}
        </TableRow>
      )}

      {/* SEGMENTS */}
      {(!showOnlyDifferences || hasAnyDifference<string>(segmentsJoined)) && (
        <TableRow sx={{ mx: 4, height: '60px' }}>
          <TableCell
            sx={{
              fontWeight: 700,
              fontSize: '14px',
              borderLeft: '1px solid',
              borderColor: '#C7D8F9',
            }}
          >
            Segment
          </TableCell>
          {plans.map((plan, index) => {
            const segText = segmentsJoined[index];
            const diff = isDifferentFromBase<string>(segmentsJoined, index);

            return (
              <TableCell
                key={`seg-${plan.id}`}
                sx={{
                  borderLeft: '1px solid',
                  borderRight: '1px solid',
                  borderColor: '#C7D8F9',
                  whiteSpace: 'pre-line',
                  fontWeight: 400,
                  fontSize: '12px',
                  textAlign: 'center',
                  verticalAlign: 'center',
                  color: diff ? 'error.main' : '#525F81',
                }}
              >
                {segText}
              </TableCell>
            );
          })}
        </TableRow>
      )}

      {/* TV */}
      {(!showOnlyDifferences || hasAnyDifference<string>(tvStatusStrings)) && (
        <TableRow sx={{ mx: 4, bgColor: '#F5F5F5', height: '60px' }}>
          <TableCell
            sx={{
              fontWeight: 700,
              fontSize: '14px',
              borderLeft: '1px solid',
              borderColor: '#C7D8F9',
            }}
          >
            TV
          </TableCell>
          {plans.map((plan, index) => {
            const { main, sub } = tvStatusValues[index];
            const diff = isDifferentFromBase<string>(tvStatusStrings, index);

            return (
              <TableCell
                key={`tv-${plan.id}`}
                sx={{
                  borderLeft: '1px solid',
                  borderRight: '1px solid',
                  borderColor: '#C7D8F9',
                  whiteSpace: 'pre-line',
                  fontWeight: 400,
                  fontSize: '12px',
                  textAlign: 'center',
                  verticalAlign: 'center',
                  color: diff ? 'error.main' : '#525F81',
                }}
              >
                {main}
                {sub ? `\n${sub}` : ''}
              </TableCell>
            );
          })}
        </TableRow>
      )}

      {/* DIGITAL BUDGET HEADER */}
      {(!showOnlyDifferences ||
        hasAnyDifference<number>(digitalBudgetSums)) && (
        <TableRow
          sx={{
            bgcolor: '#EBEBEB',
            height: '60px',
            borderLeft: '1px solid',
            borderRight: '1px solid',
            borderColor: '#C7D8F9',
          }}
        >
          <TableCell
            sx={{
              fontWeight: 700,
              fontSize: '16px',
              textTransform: 'uppercase',
              borderColor: '#C7D8F9',
            }}
          >
            DIGITAL BUDGET
          </TableCell>
          {plans.map((plan, index) => {
            const { sum, currency } = digitalBudgetTotals[index];
            const diff = isDifferentFromBase<number>(digitalBudgetSums, index);

            return (
              <TableCell
                key={`budget-${plan.id}`}
                sx={{
                  borderLeft: '1px solid',
                  borderColor: '#C7D8F9',
                  whiteSpace: 'pre-line',
                  fontWeight: 700,
                  fontSize: '16px',
                  textAlign: 'center',
                  verticalAlign: 'center',
                  color: diff ? 'error.main' : '#525F81',
                }}
              >
                {sum > 0 ? formatBudget(sum, currency) : 'N/A'}
              </TableCell>
            );
          })}
        </TableRow>
      )}
    </>
  );
}
